import type { ReactNode } from "react";
import BasicDetails from "./components/BasicDetails/BasicDetails";
import Preference from "./components/Preference/Preference";
import LeadInvisibilityBanner from "./components/LeadInvisibilityBanner";
import StageSection from "./components/StageSection";
import LeadSource from "./components/LeadSouce/LeadSource";
import NoteSection from "./components/NoteSection";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { lead_spesific_key, child_spesific_key } from "@/config/endpoints";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import ScopeProfileView from "@/components/ui/others/ModuleScopeRenderers/ScopeProfileView";
import LeadPriority from "./components/LeadPriority";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useLeadFormLockedFields } from "@/features/leads/api/useLeadFormConfig";
import { useViewLeadFieldVisibility } from "@/features/leads/hook/useViewLeadFieldVisibility";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";
import { useDealsDisabled } from "@/features/leads/api/useLeadFormConfig";
import { useAppSelector } from "@/stores/hook";
import SortableFieldGrid from "./components/SortableFieldGrid";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/library/card";
import ActiveChildModuleAttachments from "./components/ChildModuleAttachments/ActiveChildModuleAttachments";
import { useLeadToolBlocks } from "@/features/connectedTools/components/lead/LeadToolsTab";

export default function Overview() {
  const { activeData, lead, child, isChildActive } = useActiveDataMutation();
  const { spesificFields } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    stageId: lead?.stage?.id,
    campaignId: lead?.campaign?.id,
  });
  const { spesificFields: spesificsChild } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    stageId: child?.stage?.id,
    isActive: isChildActive,
  });

  const { updateLead } = useLeadMutations();
  const { isFieldLocked } = useLeadFormLockedFields();
  const { isFieldVisible } = useViewLeadFieldVisibility();
  // The Overview's BLOCKS keep a tenant-wide order a Super Admin arranges by
  // dragging them in the drawer's edit mode — the same mechanism as the
  // fields inside each card, so the "Attach …" slot can be placed wherever
  // it belongs (owner, 2026-10-16: "a provision to place it where I want,
  // just like the other fields").
  const { isEditMode } = useAppSelector((state) => state.leadFunctional);
  const { sortByOrder, canReorder, save: saveOrder } = useLeadViewFieldOrder("overview");
  const dealsDisabled = useDealsDisabled();
  const toolBlocks = useLeadToolBlocks(lead?.id, lead);

  const onPriorityChange = async (priority: any) => {
    // Priority is a one-click control with no edit mode to gate, so the lock
    // has to be checked here. The server refuses it too; this just avoids a
    // pointless round-trip and an unexplained 403 toast.
    if (isFieldLocked("basic:priority")) {
      toast.error("Priority is locked for editing by your administrator");
      return;
    }
    const data = { priority };

    updateLead.mutate({
      id: lead.id,
      data,
    });
  };

  if (!activeData) return null;

  const blocks: { key: string; node: ReactNode }[] = [];
  if (isFieldVisible("priority")) {
    blocks.push({
      key: "priority",
      node: (
        <Card className="border-[#e6e8e7] rounded-[12px] py-2 shadow-none">
          <CardContent className="px-3 py-0">
            <LeadPriority
              defaultValue={lead.priority}
              callBack={onPriorityChange}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </CardContent>
        </Card>
      ),
    });
  }
  blocks.push({ key: "basic", node: <BasicDetails /> });
  if (spesificFields?.length || spesificsChild?.length) {
    blocks.push({ key: "preference", node: <Preference /> });
  }
  if (isFieldVisible("modules") && lead.modules) {
    blocks.push({ key: "modules", node: <ScopeProfileView modules={lead.modules} /> });
  }
  // Renders for the active deal — and, on a deal-less lead in a pipeline
  // with Choices, as the "Attach … to create the first deal" slots. The
  // display toggle hides only the attached-deal cards; the create slot
  // always shows (owner, 2026-10-16: "show attach thing all the time").
  // Gone entirely under Settings → Manage → "Disable deals".
  if (!dealsDisabled) {
    blocks.push({
      key: "dealAttachments",
      node: <ActiveChildModuleAttachments showAttached={isFieldVisible("dealAttachments")} />,
    });
  }
  if (isFieldVisible("stageStatus")) {
    blocks.push({ key: "stageStatus", node: <StageSection item={activeData} /> });
  }
  blocks.push({ key: "leadSource", node: <LeadSource /> });
  // Values a connected tool placed here. Ordered with the other blocks.
  blocks.push(...toolBlocks);
  if (isFieldVisible("notes")) {
    blocks.push({ key: "notes", node: <NoteSection /> });
  }
  const ordered = sortByOrder(blocks, (b) => b.key);

  // Edit mode, Super Admin: every block gets a grip and a drop re-saves the
  // tenant-wide order. Everyone else (and view mode) renders the same order.
  if (isEditMode && canReorder) {
    return (
      <div className="flex flex-col gap-2 w-full px-3 py-3">
        <LeadInvisibilityBanner />
        <SortableFieldGrid
          items={ordered.map((b) => ({ key: b.key, node: b.node }))}
          columns={1}
          canReorder
          onReorder={saveOrder}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full px-3 py-3">
      <LeadInvisibilityBanner />
      {ordered.map((b) => (
        <div key={b.key}>{b.node}</div>
      ))}
    </div>
  );
}
